import React, { Component } from "react";
import {
  Button, Input, Select, Tag, Progress,
  notification, Spin, Row, Col, Tooltip, Slider,
} from "antd";
import {
  LoadingOutlined, CheckCircleFilled, RocketOutlined,
  HomeOutlined, EnvironmentOutlined, HeartOutlined,
  DollarOutlined, TeamOutlined, StarOutlined,
  CloseOutlined, SearchOutlined,
} from "@ant-design/icons";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { update } from "../api/Apis";
import { withAppContext } from "../store/contextStore";

const { Option } = Select;
const { TextArea } = Input;

// ── Steps ─────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: "location",   icon: <EnvironmentOutlined />,  label: "Location"      },
  { id: "lifestyle",  icon: <HeartOutlined />,        label: "Lifestyle"     },
  { id: "finances",   icon: <DollarOutlined />,       label: "Finances"      },
  { id: "situation",  icon: <TeamOutlined />,         label: "Your Situation" },
  { id: "dealmakers", icon: <StarOutlined />,         label: "Must Haves"    },
  { id: "preview",    icon: <RocketOutlined />,       label: "Preview"       },
];

// ── Option lists ──────────────────────────────────────────────────────────────
const HOME_STYLES = [
  "Colonial","Cape Cod","Ranch","Split Level","Contemporary",
  "Victorian","Craftsman","Tudor","Log Home","Townhouse",
  "Condo","Multi-Family","New Construction","Any style",
];

const PROPERTY_TYPES = [
  "Single Family","Condo / Townhouse","Multi-Family (2-4 units)",
  "55+ Community","New Construction","Land / Lot","Any type",
];

const COMMUTE_MODES = [
  "🚗 Drive","🚆 Train / MBTA","🚌 Bus","🚴 Bike","🚶 Walk","🏠 Work from home",
];

const SCHOOL_PRIORITIES = [
  "Top rated public schools","Good public schools","Private school access",
  "Special needs programs","Pre-K / daycare nearby","No kids — not a factor",
];

const HOBBIES = [
  "🏃 Running / Walking trails","⛳ Golf","🎾 Tennis","🏊 Pool / Swimming",
  "🚴 Cycling","🏔️ Hiking","🐾 Dog-friendly neighborhood","🌱 Gardening / Yard space",
  "🍳 Cooking / Entertaining","🎵 Music / Arts scene","🍷 Restaurants / Nightlife",
  "🛍️ Shopping nearby","🏋️ Gym / Fitness","⛷️ Ski access","🚣 Water / Lake access",
  "🌳 Nature / Privacy","🏈 Sports fan","📚 Library / Community center",
];

const NEIGHBORHOOD_VIBES = [
  "Quiet suburban","Walkable downtown","Rural / Country","Waterfront / Lake",
  "Historic / Character","New development","Diverse community",
  "Young families","Active adult (55+)","College town",
];

const DEAL_BREAKERS = [
  "No HOA","Must have garage","Must have basement","Must have yard",
  "No flood zone","No busy road","No condo fees","Must have pool",
  "Single floor living","Must have home office","Must have in-law suite",
  "No septic / city sewer only","Solar ready","EV charging",
];

const MUST_HAVES = [
  "Open floor plan","Updated kitchen","Updated bathrooms","Hardwood floors",
  "Central A/C","Finished basement","First floor bedroom","Large master suite",
  "Home office / study","3+ car garage","Oversized yard","Deck / Patio",
  "Fireplace","Smart home features","Large storage","Mudroom / Laundry room",
];

const LIFE_STAGES = [
  "First-time buyer","Moving up / Upsizing","Downsizing",
  "Relocating for work","Investment / Rental","Retirement home",
  "Vacation / Second home","Building equity",
];

const TIMELINES = [
  "Ready to move ASAP","Within 1-3 months","3-6 months",
  "6-12 months","Just exploring for now",
];

const FINANCING = [
  "Pre-approved — ready to go","Getting pre-approved now",
  "Saving for down payment","Cash buyer","Haven't start the loan process yet",
];

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const BG     = "#F7F9FC";
const BORDER = "#E2E8F0";
const MUTED  = "#718096";
const GREEN  = "#38A169";

const chip = (active) => ({
  display: "inline-block", padding: "6px 14px", borderRadius: "20px",
  fontSize: "13px", cursor: "pointer", margin: "4px",
  fontWeight: active ? 700 : 400,
  background: active ? NAVY : "#fff",
  color: active ? "#fff" : "#1A202C",
  border: `1.5px solid ${active ? NAVY : BORDER}`,
  transition: "all .15s",
});

const goldChip = (active) => ({
  ...chip(active),
  background: active ? GOLD : "#fff",
  color: active ? "#2A1600" : "#1A202C",
  border: `1.5px solid ${active ? GOLD : BORDER}`,
});

const btn = (variant) => ({
  borderRadius: "50px", fontWeight: 700, fontSize: "14px",
  height: "42px", padding: "0 24px",
  background: variant === "primary" ? NAVY : variant === "gold" ? GOLD : "transparent",
  color: variant === "primary" ? "#fff" : variant === "gold" ? "#2A1600" : MUTED,
  border: variant === "ghost" ? `1.5px solid ${BORDER}` : "none",
});

// ── Component ─────────────────────────────────────────────────────────────────
class BuyerProfileModal extends Component {
  constructor(props) {
    super(props);
    const ud = props.userDetails || {};
    const bp = ud.buyerProfile || {};

    this.state = {
      step:    0,
      saving:  false,
      aiLoading: false,
      aiMatchPreview: "",

      form: {
        // Step 1 — Home Needs
        minBeds:       bp.minBeds       || 2,
        minBaths:      bp.minBaths      || 1,
        minSqft:       bp.minSqft       || "",
        maxSqft:       bp.maxSqft       || "",
        priceMin:      bp.priceMin      || "",
        priceMax:      bp.priceMax      || "",
        propertyTypes: bp.propertyTypes || [],
        homeStyles:    bp.homeStyles    || [],
        garageSpaces:  bp.garageSpaces  || "any",
        basement:      bp.basement      || "",
        acreageMin:    bp.acreageMin    || "",

        // Step 2 — Location
        targetCities:   bp.targetCities   || [],
        targetZips:     bp.targetZips     || [],
        zipInput:       "",
        workAddress:    bp.workAddress    || "",
        maxCommuteMins: bp.maxCommuteMins || 30,
        commuteModes:   bp.commuteModes   || [],
        schoolPriority: bp.schoolPriority || [],
        neighborhoodVibes: bp.neighborhoodVibes || [],
        maxDistanceToHighway: bp.maxDistanceToHighway || "",

        // Step 3 — Lifestyle
        hobbies:          bp.hobbies          || [],
        entertainingStyle:bp.entertainingStyle || "",
        outdoorSpace:     bp.outdoorSpace      || "",
        petFriendly:      bp.petFriendly       || false,
        petDetails:       bp.petDetails        || "",
        walkabilityScore: bp.walkabilityScore  || 3,
        nearbyWants:      bp.nearbyWants       || [],

        // Step 4 — Finances
        financing:     bp.financing     || "",
        downPayment:   bp.downPayment   || "",
        monthlyBudget: bp.monthlyBudget || "",
        includesTaxes: bp.includesTaxes || true,
        firstTimeBuyer:bp.firstTimeBuyer|| false,
        vaEligible:    bp.vaEligible    || false,

        // Step 5 — Situation
        lifeStage:    bp.lifeStage    || "",
        timeline:     bp.timeline     || "",
        currentSituation: bp.currentSituation || "",
        householdSize: bp.householdSize || "",
        hasPets:       bp.hasPets      || false,
        hasKids:       bp.hasKids      || false,
        workFromHome:  bp.workFromHome || false,
        additionalNotes: bp.additionalNotes || "",

        // Step 6 — Deal breakers / Must haves
        dealBreakers: bp.dealBreakers || [],
        mustHaves:    bp.mustHaves    || [],
        niceToHaves:  bp.niceToHaves  || [],
        absoluteNos:  bp.absoluteNos  || "",
      },
    };
  }

  setForm = (field, val) =>
    this.setState(p => ({ form: { ...p.form, [field]: val } }));

  toggleChip = (field, val, max = 99) =>
    this.setState(p => {
      const arr = p.form[field] || [];
      const has = arr.includes(val);
      if (!has && arr.length >= max) return p;
      return { form: { ...p.form, [field]: has ? arr.filter(x => x !== val) : [...arr, val] } };
    });

  calcStrength = () => {
    const { form } = this.state;
    const checks = [
      form.targetCities.length > 0 || form.targetZips.length > 0,
      form.commuteModes.length > 0 || form.workFromHome,
      form.neighborhoodVibes.length > 0,
      form.hobbies.length > 0,
      form.financing,
      form.lifeStage,
      form.timeline,
      form.dealBreakers.length > 0 || form.mustHaves.length > 0,
      form.schoolPriority.length > 0,
    ];
    return Math.round(checks.filter(Boolean).length / checks.length * 100);
  };

  handleDismiss = async () => {
    const { form } = this.state;
    const ud = this.props.userDetails || {};
    try {
      if (ud.id) {
        await update("users", ud.id, {
          entityData: { buyerProfile: form, buyerProfileStarted: true }
        });
      }
    } catch(e) { console.warn("Dismiss save:", e.message); }
    if (this.props.onDismiss) this.props.onDismiss();
    else if (this.props.onComplete) this.props.onComplete();
  };

  handleSave = async () => {
    const { form } = this.state;
    const ud = this.props.userDetails || {};
    const strength = this.calcStrength();
    this.setState({ saving: true });
    try {
      const payload = {
        entityData: {
          buyerProfile: { ...form, zipInput: undefined },
          buyerProfileCompleted: strength >= 75,
          buyerProfileStrength: strength,
        }
      };
      const result = await update("users", ud.id, payload);
      if (result.success) {
        const updated = { ...ud, ...payload.entityData };
        localStorage.setItem("userDetails", JSON.stringify(updated));
        this.props.context.setUserDetails(updated);
        if (strength >= 75) {
          notification.success({ message: "Buyer profile complete! 🎉", description: "We'll now show you AI match scores on every listing." });
        } else {
          notification.info({ message: `Profile saved — ${strength}% complete`, description: "Finish anytime in your profile settings to unlock AI match scores.", duration: 5 });
        }
        this.props.onComplete();
      } else throw new Error(result.errors);
    } catch(e) {
      notification.error({ message: "Save failed", description: e.message });
    }
    this.setState({ saving: false });
  };

  generateAIPreview = async () => {
    const { form } = this.state;
    this.setState({ aiLoading: true, aiMatchPreview: "" });
    try {
      const resp = await fetch("https://ai.myhomebuddha.com/.netlify/functions/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: "You are a real estate AI assistant. Based on a buyer profile, generate a short 3-4 sentence summary of what their ideal home looks like and what's most important to them. Sound warm and personal, not robotic.",
          messages: [{
            role: "user",
            content: `Buyer profile:
Price range: $${form.priceMin || "any"} - $${form.priceMax || "any"}
Beds: ${form.minBeds}+ | Baths: ${form.minBaths}+
Property types: ${form.propertyTypes.join(", ") || "any"}
Target areas: ${form.targetCities.join(", ") || "flexible"}
Commute: ${form.maxCommuteMins} min max via ${form.commuteModes.join(", ") || "any"}
Neighborhood vibe: ${form.neighborhoodVibes.join(", ") || "flexible"}
Hobbies: ${form.hobbies.join(", ") || "not specified"}
Life stage: ${form.lifeStage || "not specified"}
Timeline: ${form.timeline || "not specified"}
Must haves: ${form.mustHaves.join(", ") || "none specified"}
Deal breakers: ${form.dealBreakers.join(", ") || "none specified"}
Additional notes: ${form.additionalNotes || "none"}
Write a warm 3-4 sentence summary of their ideal home.`
          }]
        })
      });
      const data = await resp.json();
      const text = data.content?.[0]?.text || "";
      this.setState({ aiMatchPreview: text, aiLoading: false });
    } catch(e) {
      this.setState({ aiLoading: false });
    }
  };

  // ── Step renderers ────────────────────────────────────────────────────────

  renderBasics() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>What are you looking for in a home?</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>Start with the basics — you can always adjust these later.</div>

        {/* Price range */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Price Range</label>
          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Input prefix="$" placeholder="Min price" value={form.priceMin}
                onChange={e => this.setForm("priceMin", e.target.value)}
                style={{ borderRadius: "10px" }} />
            </Col>
            <Col span={12}>
              <Input prefix="$" placeholder="Max price" value={form.priceMax}
                onChange={e => this.setForm("priceMax", e.target.value)}
                style={{ borderRadius: "10px" }} />
            </Col>
          </Row>
        </div>

        {/* Beds / Baths */}
        <Row gutter={[12, 0]} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Minimum Bedrooms</label>
            <Select value={form.minBeds} onChange={v => this.setForm("minBeds", v)} style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {[1,2,3,4,5].map(n => <Option key={n} value={n}>{n}+ beds</Option>)}
            </Select>
          </Col>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Minimum Bathrooms</label>
            <Select value={form.minBaths} onChange={v => this.setForm("minBaths", v)} style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {[1,1.5,2,2.5,3,4].map(n => <Option key={n} value={n}>{n}+ baths</Option>)}
            </Select>
          </Col>
        </Row>

        {/* Sqft */}
        <Row gutter={[12, 0]} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Min Square Footage</label>
            <Input placeholder="e.g. 1200" value={form.minSqft} onChange={e => this.setForm("minSqft", e.target.value)} style={{ borderRadius: "10px" }} suffix="sqft" />
          </Col>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Max Square Footage</label>
            <Input placeholder="e.g. 3000" value={form.maxSqft} onChange={e => this.setForm("maxSqft", e.target.value)} style={{ borderRadius: "10px" }} suffix="sqft" />
          </Col>
        </Row>

        {/* Property Types */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Property Type</label>
          {PROPERTY_TYPES.map(t => (
            <span key={t} style={chip(form.propertyTypes.includes(t))} onClick={() => this.toggleChip("propertyTypes", t)}>{t}</span>
          ))}
        </div>

        {/* Home Styles */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Home Style <span style={{ color: MUTED, fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          {HOME_STYLES.map(s => (
            <span key={s} style={chip(form.homeStyles.includes(s))} onClick={() => this.toggleChip("homeStyles", s)}>{s}</span>
          ))}
        </div>

        {/* Garage */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Garage</label>
          <Select value={form.garageSpaces} onChange={v => this.setForm("garageSpaces", v)} style={{ width: "220px" }} getPopupContainer={t => t.parentNode}>
            <Option value="any">Not required</Option>
            <Option value="1">1+ car garage</Option>
            <Option value="2">2+ car garage</Option>
            <Option value="3">3+ car garage</Option>
          </Select>
        </div>
      </div>
    );
  }

  renderLocation() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Where do you want to live?</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>Location preferences, commute, and neighborhood vibe.</div>

        {/* Target Cities */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Target Towns / Cities</label>
          <div onKeyDown={e => e.stopPropagation()}>
            <GooglePlacesAutocomplete
              apiKey={process.env.REACT_APP_MAPS_KEY}
              autocompletionRequest={{ types: ["(cities)"], componentRestrictions: { country: ["us"] } }}
              selectProps={{
                placeholder: "Add a town or city...",
                onChange: (result) => {
                  const city = result.label.split(",")[0].trim();
                  if (!form.targetCities.includes(city)) this.setForm("targetCities", [...form.targetCities, city]);
                },
              }}
            />
          </div>
          <div style={{ marginTop: "8px" }}>
            {form.targetCities.map(c => (
              <Tag key={c} closable onClose={() => this.setForm("targetCities", form.targetCities.filter(x => x !== c))}
                style={{ marginBottom: "4px", borderRadius: "20px" }}>{c}</Tag>
            ))}
          </div>
        </div>

        {/* Zip codes */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Specific Zip Codes <span style={{ color: MUTED, fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <div style={{ display: "flex", gap: "8px" }}>
            <Input value={form.zipInput} onChange={e => this.setForm("zipInput", e.target.value)}
              placeholder="e.g. 01453" style={{ borderRadius: "10px", maxWidth: "140px" }}
              onPressEnter={() => {
                const z = form.zipInput.trim();
                if (z && !form.targetZips.includes(z)) {
                  this.setState(p => ({ form: { ...p.form, targetZips: [...p.form.targetZips, z], zipInput: "" } }));
                }
              }} />
            <Button style={{ borderRadius: "50px" }} onClick={() => {
              const z = form.zipInput.trim();
              if (z && !form.targetZips.includes(z)) {
                this.setState(p => ({ form: { ...p.form, targetZips: [...p.form.targetZips, z], zipInput: "" } }));
              }
            }}>Add</Button>
          </div>
          <div style={{ marginTop: "8px" }}>
            {form.targetZips.map(z => (
              <Tag key={z} closable onClose={() => this.setForm("targetZips", form.targetZips.filter(x => x !== z))}
                style={{ marginBottom: "4px", borderRadius: "20px" }}>{z}</Tag>
            ))}
          </div>
        </div>

        {/* Work address + commute */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Work Address <span style={{ color: MUTED, fontWeight: 400, textTransform: "none" }}>(for commute matching)</span></label>
          <Input value={form.workAddress} onChange={e => this.setForm("workAddress", e.target.value)}
            placeholder="e.g. 100 Summer St, Boston MA" style={{ borderRadius: "10px" }} />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Max Commute: <strong style={{ color: NAVY }}>{form.maxCommuteMins} minutes</strong></label>
          <Slider min={5} max={90} step={5} value={form.maxCommuteMins}
            onChange={v => this.setForm("maxCommuteMins", v)}
            marks={{ 5: "5m", 30: "30m", 60: "1hr", 90: "90m" }}
            trackStyle={{ background: NAVY }} handleStyle={{ borderColor: NAVY }} />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>How do you commute?</label>
          {COMMUTE_MODES.map(m => (
            <span key={m} style={chip(form.commuteModes.includes(m))} onClick={() => this.toggleChip("commuteModes", m)}>{m}</span>
          ))}
        </div>

        {/* Schools */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>School Priorities</label>
          {SCHOOL_PRIORITIES.map(s => (
            <span key={s} style={chip(form.schoolPriority.includes(s))} onClick={() => this.toggleChip("schoolPriority", s)}>{s}</span>
          ))}
        </div>

        {/* Neighborhood vibe */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Neighborhood Vibe</label>
          {NEIGHBORHOOD_VIBES.map(v => (
            <span key={v} style={chip(form.neighborhoodVibes.includes(v))} onClick={() => this.toggleChip("neighborhoodVibes", v, 3)}>{v}</span>
          ))}
        </div>
      </div>
    );
  }

  renderLifestyle() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Tell us about your lifestyle</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "6px", lineHeight: 1.6 }}>
          This is what makes your AI match score powerful. The more we know about how you live, the better we can match you.
        </div>
        <div style={{ background: `${NAVY}08`, borderRadius: "10px", padding: "10px 14px", marginBottom: "20px", fontSize: "12px", color: NAVY, borderLeft: `3px solid ${GOLD}` }}>
          🤖 Your hobbies and lifestyle tell us things like: does this home need trail access? Is the neighborhood walkable to restaurants? Is there space to entertain?
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Hobbies & Interests</label>
          {HOBBIES.map(h => (
            <span key={h} style={chip(form.hobbies.includes(h))} onClick={() => this.toggleChip("hobbies", h)}>{h}</span>
          ))}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>
            How important is walkability to you?
            <strong style={{ color: NAVY, marginLeft: "8px" }}>
              {["Not important","Nice to have","Somewhat important","Very important","Essential"][form.walkabilityScore - 1]}
            </strong>
          </label>
          <Slider min={1} max={5} step={1} value={form.walkabilityScore}
            onChange={v => this.setForm("walkabilityScore", v)}
            marks={{ 1: "Not important", 3: "Somewhat", 5: "Essential" }}
            trackStyle={{ background: NAVY }} handleStyle={{ borderColor: NAVY }} />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Do you have pets?</label>
          <div style={{ display: "flex", gap: "10px" }}>
            {["Yes","No"].map(v => (
              <span key={v} style={chip((form.hasPets ? "Yes" : "No") === v)} onClick={() => this.setForm("hasPets", v === "Yes")}>{v}</span>
            ))}
          </div>
          {form.hasPets && (
            <Input style={{ marginTop: "10px", borderRadius: "10px" }} placeholder="Tell us about your pets (e.g. 2 large dogs)"
              value={form.petDetails} onChange={e => this.setForm("petDetails", e.target.value)} />
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Do you work from home?</label>
          <div style={{ display: "flex", gap: "10px" }}>
            {["Yes — full time","Sometimes","No"].map(v => (
              <span key={v} style={chip(form.workFromHome === v)} onClick={() => this.setForm("workFromHome", v)}>{v}</span>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Anything else about your lifestyle we should know?</label>
          <TextArea rows={3} value={form.additionalNotes} onChange={e => this.setForm("additionalNotes", e.target.value)}
            placeholder="e.g. We host large family gatherings, need space for aging parent, run a home business..."
            style={{ borderRadius: "10px" }} />
        </div>
      </div>
    );
  }

  renderFinances() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Let's talk finances</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>This helps us filter listings to what's realistic for you and match you with lenders.</div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Where are you with financing?</label>
          {FINANCING.map(f => (
            <div key={f} style={{ marginBottom: "8px" }}>
              <span style={chip(form.financing === f)} onClick={() => this.setForm("financing", f)}>{f}</span>
            </div>
          ))}
        </div>

        <Row gutter={[12, 0]} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Down Payment Available</label>
            <Select value={form.downPayment || undefined} onChange={v => this.setForm("downPayment", v)}
              placeholder="Select range" style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {["Less than 5%","5-10%","10-20%","20%+","Full cash purchase"].map(r => <Option key={r} value={r}>{r}</Option>)}
            </Select>
          </Col>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Monthly Payment Budget</label>
            <Input prefix="$" placeholder="e.g. 2500" value={form.monthlyBudget}
              onChange={e => this.setForm("monthlyBudget", e.target.value)}
              style={{ borderRadius: "10px" }} suffix="/mo" />
          </Col>
        </Row>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Special Programs</label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {[
              { label: "First-time buyer", field: "firstTimeBuyer" },
              { label: "VA eligible", field: "vaEligible" },
            ].map(({ label, field }) => (
              <span key={field} style={chip(form[field])} onClick={() => this.setForm(field, !form[field])}>{label}</span>
            ))}
          </div>
        </div>

        {/* Lender CTA */}
        {form.financing !== "Pre-approved — ready to go" && form.financing !== "Cash buyer" && (
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "12px", padding: "16px 20px" }}>
            <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "13px", marginBottom: "6px" }}>💰 Not pre-approved yet?</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", lineHeight: 1.6, marginBottom: "12px" }}>
              Getting pre-approved takes 15 minutes and dramatically increases your chances of winning an offer. Connect with a My Home Buddha preferred lender.
            </div>
            <button style={{ background: GOLD, color: "#2A1600", border: "none", borderRadius: "50px", padding: "8px 18px", fontWeight: 800, fontSize: "12px", cursor: "pointer" }}
              onClick={() => window.open("/lenders", "_blank")}>
              Find a Lender →
            </button>
          </div>
        )}
      </div>
    );
  }

  renderSituation() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Your situation</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>Help us understand where you are in your journey.</div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Why are you buying?</label>
          {LIFE_STAGES.map(s => (
            <div key={s} style={{ marginBottom: "6px" }}>
              <span style={chip(form.lifeStage === s)} onClick={() => this.setForm("lifeStage", s)}>{s}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>What's your timeline?</label>
          {TIMELINES.map(t => (
            <div key={t} style={{ marginBottom: "6px" }}>
              <span style={goldChip(form.timeline === t)} onClick={() => this.setForm("timeline", t)}>{t}</span>
            </div>
          ))}
        </div>

        <Row gutter={[12, 0]} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Household Size</label>
            <Select value={form.householdSize || undefined} onChange={v => this.setForm("householdSize", v)}
              placeholder="Number of people" style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {["1","2","3","4","5","6+"].map(n => <Option key={n} value={n}>{n} {n === "1" ? "person" : "people"}</Option>)}
            </Select>
          </Col>
          <Col span={12}>
            <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Children at home?</label>
            <Select value={form.hasKids ? "yes" : "no"} onChange={v => this.setForm("hasKids", v === "yes")} style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </Col>
        </Row>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Current situation</label>
          <Select value={form.currentSituation || undefined} onChange={v => this.setForm("currentSituation", v)}
            placeholder="Where are you currently?" style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
            {["Renting month-to-month","Renting with lease ending soon","Own a home — need to sell first",
              "Own a home — keeping it","Living with family","Relocating from another state","Relocating from another country"].map(s => (
              <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>
        </div>
      </div>
    );
  }

  renderDealmakers() {
    const { form } = this.state;
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Must haves & deal breakers</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>Be specific — this directly filters your AI match score.</div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: "#E53E3E", textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>🚫 Deal Breakers — I will NOT consider a home without these</label>
          {DEAL_BREAKERS.map(d => (
            <span key={d} style={{
              ...chip(form.dealBreakers.includes(d)),
              background: form.dealBreakers.includes(d) ? "#E53E3E" : "#fff",
              borderColor: form.dealBreakers.includes(d) ? "#E53E3E" : BORDER,
            }} onClick={() => this.toggleChip("dealBreakers", d)}>{d}</span>
          ))}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: GREEN, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>✅ Must Haves — strongly prefer these</label>
          {MUST_HAVES.map(m => (
            <span key={m} style={{
              ...chip(form.mustHaves.includes(m)),
              background: form.mustHaves.includes(m) ? GREEN : "#fff",
              borderColor: form.mustHaves.includes(m) ? GREEN : BORDER,
            }} onClick={() => this.toggleChip("mustHaves", m)}>{m}</span>
          ))}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: "8px" }}>Anything else that's a hard no?</label>
          <TextArea rows={2} value={form.absoluteNos} onChange={e => this.setForm("absoluteNos", e.target.value)}
            placeholder="e.g. No oil heat, no pools, not near a highway..."
            style={{ borderRadius: "10px" }} />
        </div>
      </div>
    );
  }

  renderPreview() {
    const { aiMatchPreview, aiLoading } = this.state;
    const strength = this.calcStrength();
    return (
      <div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: NAVY, marginBottom: "4px" }}>Your Buyer Profile</div>
        <div style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>Review your profile strength and see your AI-generated home summary.</div>

        {/* Strength meter */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Progress type="circle" percent={strength}
            strokeColor={strength >= 75 ? GREEN : GOLD}
            trailColor={BORDER} strokeWidth={10}
            format={pct => (
              <div>
                <div style={{ fontSize: "24px", fontWeight: 800, color: pct >= 75 ? GREEN : GOLD }}>{pct}%</div>
                <div style={{ fontSize: "11px", color: MUTED }}>{pct >= 75 ? "Ready!" : "Keep going"}</div>
              </div>
            )} width={120} />
          <div style={{ marginTop: "10px", fontSize: "13px", color: MUTED }}>
            {strength >= 75
              ? "Your profile is complete — AI match scores are now active on every listing!"
              : `${100 - strength}% more to unlock full AI match scoring. You can save and finish later.`}
          </div>
        </div>

        {/* AI Summary */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: ".05em" }}>🤖 Your AI Home Summary</label>
          <Button onClick={this.generateAIPreview} loading={aiLoading} size="small"
            style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY, fontSize: "12px" }}>
            {aiMatchPreview ? "Regenerate" : "✨ Generate"}
          </Button>
        </div>

        {aiLoading && (
          <div style={{ textAlign: "center", padding: "30px", color: MUTED }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: "12px" }}>Writing your home summary...</div>
          </div>
        )}
        {aiMatchPreview && !aiLoading && (
          <div style={{ background: "#fff", borderRadius: "14px", border: `1.5px solid ${BORDER}`, padding: "20px", fontSize: "14px", lineHeight: 1.7, fontFamily: "Georgia,serif", whiteSpace: "pre-wrap", marginBottom: "16px" }}>
            {aiMatchPreview}
          </div>
        )}
        {!aiMatchPreview && !aiLoading && (
          <div style={{ background: "#fff", borderRadius: "14px", border: `1.5px solid ${BORDER}`, padding: "40px", textAlign: "center", color: MUTED, fontStyle: "italic", marginBottom: "16px" }}>
            Click Generate to see your AI-written home summary
          </div>
        )}

        {strength >= 75 && (
          <div style={{ padding: "16px", background: "#F0FFF4", borderRadius: "12px", border: `1.5px solid ${GREEN}`, textAlign: "center" }}>
            <CheckCircleFilled style={{ color: GREEN, fontSize: "20px", marginBottom: "6px", display: "block" }} />
            <div style={{ fontWeight: 700, color: GREEN }}>Profile ready!</div>
            <div style={{ fontSize: "13px", color: MUTED, marginTop: "4px" }}>Every listing you view will now show an AI match score and explanation.</div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { step, saving } = this.state;
    const isLast = step === STEPS.length - 1;
    const strength = this.calcStrength();

    const stepContent = [
      this.renderLocation(),
      this.renderLifestyle(),
      this.renderFinances(),
      this.renderSituation(),
      this.renderDealmakers(),
      this.renderPreview(),
    ][step];

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,15,40,0.78)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
        <div style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "780px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(26,34,128,0.25)", overflow: "hidden" }}>

          {/* Header */}
          <div style={{ background: `linear-gradient(135deg,${NAVY},#2A3390)`, padding: "18px 28px 14px", borderBottom: `3px solid ${GOLD}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div>
                <div style={{ color: GOLD, fontWeight: 800, fontSize: "18px", letterSpacing: "-0.02em" }}>My Home Buddha</div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", marginTop: "2px" }}>Build your buyer profile to unlock AI-powered home matching</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "20px", padding: "4px 12px", color: "#fff", fontSize: "12px", fontWeight: 700 }}>
                  {strength}% complete
                </div>
                <Tooltip title="Finish later — progress is saved">
                  <button onClick={this.handleDismiss}
                    style={{ background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.3)", borderRadius: "50%", width: "32px", height: "32px", cursor: "pointer", color: "#fff", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CloseOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
            {/* Step tabs */}
            <div style={{ display: "flex", gap: "4px", overflowX: "auto", paddingBottom: "2px" }}>
              {STEPS.map((st, i) => (
                <div key={st.id}
                  onClick={() => i < step && this.setState({ step: i })}
                  style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    padding: "5px 10px", borderRadius: "20px", fontSize: "11px",
                    fontWeight: i === step ? 700 : 500, whiteSpace: "nowrap",
                    background: i === step ? GOLD : i < step ? "rgba(212,146,26,0.2)" : "rgba(255,255,255,0.08)",
                    color: i === step ? "#2A1600" : i < step ? GOLD : "rgba(255,255,255,0.55)",
                    cursor: i < step ? "pointer" : "default", transition: "all .2s",
                  }}>
                  {i < step ? <CheckCircleFilled /> : st.icon}
                  <span>{st.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "28px", background: BG }}>
            {stepContent}
          </div>

          {/* Footer */}
          <div style={{ padding: "16px 28px", background: "#fff", borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ fontSize: "12px", color: MUTED }}>{isLast ? "" : `Step ${step + 1} of ${STEPS.length}`}</div>
              <button onClick={this.handleDismiss}
                style={{ background: "none", border: "none", color: MUTED, fontSize: "12px", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                Finish later
              </button>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              {step > 0 && (
                <Button onClick={() => this.setState(p => ({ step: p.step - 1 }))} style={btn("ghost")}>← Back</Button>
              )}
              {isLast ? (
                <Button onClick={this.handleSave} loading={saving} style={btn("gold")}>
                  {strength >= 75 ? "🚀 Save & Start Matching" : "💾 Save & Finish Later"}
                </Button>
              ) : (
                <Button onClick={() => this.setState(p => ({ step: p.step + 1 }))} style={btn("primary")}>
                  Continue →
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withAppContext(BuyerProfileModal);